import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Plus, Trash2, Eye, Save, Type, PenLine, CalendarDays, Hash, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_META = {
  participant_name: { label: 'Participant Name', icon: Type, color: 'border-teal-500 bg-teal-500/10 text-teal-800' },
  signature: { label: 'Signature', icon: PenLine, color: 'border-violet-500 bg-violet-500/10 text-violet-800' },
  signer_name: { label: 'Signer Name', icon: Type, color: 'border-indigo-500 bg-indigo-500/10 text-indigo-800' },
  signer_title: { label: 'Signer Title', icon: Type, color: 'border-indigo-400 bg-indigo-400/10 text-indigo-700' },
  issue_date: { label: 'Issue Date', icon: CalendarDays, color: 'border-amber-500 bg-amber-500/10 text-amber-800' },
  cert_number: { label: 'Certificate #', icon: Hash, color: 'border-slate-500 bg-slate-500/10 text-slate-700' },
  qr: { label: 'Verification QR', icon: QrCode, color: 'border-emerald-600 bg-emerald-500/10 text-emerald-800' }
};

const DEFAULTS = {
  participant_name: { w: 0.5, h: 0.07, size: 32 },
  signature: { w: 0.22, h: 0.08, size: 24 },
  signer_name: { w: 0.22, h: 0.035, size: 14 },
  signer_title: { w: 0.22, h: 0.03, size: 11 },
  issue_date: { w: 0.2, h: 0.035, size: 14 },
  cert_number: { w: 0.22, h: 0.03, size: 10 },
  qr: { w: 0.08, h: 0.11, size: 10 }
};

export const TemplateEditor = ({ API_URL, getAuthHeader, template, onBack, onSaved }) => {
  const [fields, setFields] = useState(template.fields || []);
  const [version, setVersion] = useState(template.version);
  const [activeId, setActiveId] = useState(null);
  const [bgUrl, setBgUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    let url;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/cert/templates/${template.id}/preview.png`, { headers: getAuthHeader() });
        if (!r.ok) throw new Error();
        url = URL.createObjectURL(await r.blob());
        setBgUrl(url);
      } catch { toast.error('Could not load template preview'); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [API_URL, getAuthHeader, template.id]);

  const addField = (type) => {
    const slotUsed = fields.filter(f => f.type === type).length;
    if (type === 'participant_name' && slotUsed > 0) { toast.error('Participant name is already placed'); return; }
    const signer_slot = ['signature', 'signer_name', 'signer_title'].includes(type) ? slotUsed + 1 : 1;
    if (signer_slot > 3) { toast.error('Up to 3 signer slots supported'); return; }
    const f = {
      id: crypto.randomUUID(), type, signer_slot,
      x: Math.min(0.75, 0.1 + (fields.length % 6) * 0.12), y: Math.min(0.85, 0.15 + (fields.length % 6) * 0.12),
      ...DEFAULTS[type], font: 'helv', align: 'center', color: '#000000'
    };
    setFields(fs => [...fs, f]);
    setActiveId(f.id);
  };

  const updateActive = (patch) => setFields(fs => fs.map(f => f.id === activeId ? { ...f, ...patch } : f));
  const active = fields.find(f => f.id === activeId);

  const onMouseDown = (e, f, mode) => {
    e.preventDefault(); e.stopPropagation();
    setActiveId(f.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { id: f.id, mode, startX: e.clientX, startY: e.clientY, orig: { ...f }, rect };
  };

  const onMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.rect.width;
    const dy = (e.clientY - d.startY) / d.rect.height;
    setFields(fs => fs.map(f => {
      if (f.id !== d.id) return f;
      if (d.mode === 'move') {
        return { ...f, x: Math.max(0, Math.min(1 - f.w, d.orig.x + dx)), y: Math.max(0, Math.min(1 - f.h, d.orig.y + dy)) };
      }
      return { ...f, w: Math.max(0.03, Math.min(1 - f.x, d.orig.w + dx)), h: Math.max(0.02, Math.min(1 - f.y, d.orig.h + dy)) };
    }));
  }, []);

  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [onMouseMove, onMouseUp]);

  const save = async () => {
    if (!fields.some(f => f.type === 'participant_name')) { toast.error('Place a Participant Name field before saving'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/cert/templates/${template.id}/fields`, {
        method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(`Layout saved (template v${d.version})`);
      setVersion(d.version);
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const previewSample = async () => {
    try {
      const r = await fetch(`${API_URL}/api/cert/templates/${template.id}/sample.pdf`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Preview failed — save the layout first');
      const url = URL.createObjectURL(await r.blob());
      window.open(url, '_blank', 'noopener');
    } catch (e) { toast.error(e.message); }
  };

  const aspect = template.width && template.height ? template.width / template.height : 1.33;

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-teal-700" data-testid="editor-back-btn">
          <ArrowLeft className="w-4 h-4" /> Templates
        </button>
        <div className="flex items-center gap-2">
          <button onClick={previewSample} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700" data-testid="preview-sample-btn">
            <Eye className="w-3.5 h-3.5" /> Preview Sample (saved layout)
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50" data-testid="save-fields-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Layout'}
          </button>
        </div>
      </div>
      <div>
        <h2 className="text-xl font-bold text-slate-800">{template.name} <span className="text-xs font-normal text-slate-400">v{version} · {template.orientation} · {template.file_type.toUpperCase()}</span></h2>
        <p className="text-xs text-slate-500">The uploaded design (including fixed training details) is preserved exactly — only the variable fields you place here are overlaid. Drag to move, use the corner handle to resize.</p>
        {template.low_res && <p className="text-xs text-amber-700 font-semibold mt-1">⚠ This image's resolution is low — printed certificates may look blurry.</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        {/* Canvas */}
        <div ref={canvasRef} className="relative w-full border border-slate-300 rounded-lg overflow-hidden bg-slate-100 select-none" style={{ aspectRatio: `${aspect}` }} onMouseDown={() => setActiveId(null)} data-testid="editor-canvas">
          {bgUrl ? <img src={bgUrl} alt="Certificate template background" className="absolute inset-0 w-full h-full" draggable={false} /> : <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Loading template…</div>}
          {fields.map(f => {
            const meta = FIELD_META[f.type];
            return (
              <div key={f.id}
                onMouseDown={(e) => onMouseDown(e, f, 'move')}
                className={`absolute border-2 border-dashed rounded flex items-center justify-center cursor-move ${meta.color} ${activeId === f.id ? 'ring-2 ring-offset-1 ring-teal-500' : ''}`}
                style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}
                data-testid={`field-box-${f.type}-${f.signer_slot}`}>
                <span className="text-[10px] font-bold truncate px-1 pointer-events-none">
                  {meta.label}{['signature', 'signer_name', 'signer_title'].includes(f.type) ? ` ${f.signer_slot}` : ''}
                </span>
                <div onMouseDown={(e) => onMouseDown(e, f, 'resize')} className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-white border-2 border-teal-600 rounded-sm cursor-se-resize" aria-hidden="true" />
              </div>
            );
          })}
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Add Field</h3>
            <p className="text-[10px] text-slate-400 mb-2">Optional fields stay off until you add them.</p>
            <div className="grid grid-cols-1 gap-1.5">
              {Object.entries(FIELD_META).map(([type, meta]) => {
                const Icon = meta.icon;
                return (
                  <button key={type} onClick={() => addField(type)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg hover:border-teal-400 text-slate-700 text-left" data-testid={`add-field-${type}`}>
                    <Icon className="w-3.5 h-3.5" /> {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {active && (
            <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-3" data-testid="field-props-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">{FIELD_META[active.type].label}{['signature', 'signer_name', 'signer_title'].includes(active.type) ? ` — Signer ${active.signer_slot}` : ''}</h3>
                <button onClick={() => { setFields(fs => fs.filter(f => f.id !== activeId)); setActiveId(null); }} aria-label="Remove field" className="text-rose-500 hover:text-rose-700" data-testid="remove-field-btn"><Trash2 className="w-4 h-4" /></button>
              </div>
              {['signature', 'signer_name', 'signer_title'].includes(active.type) && (
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1" htmlFor="fp-slot">Signer slot</label>
                  <select id="fp-slot" value={active.signer_slot} onChange={e => updateActive({ signer_slot: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 text-xs">
                    {[1, 2, 3].map(s => <option key={s} value={s}>Signer {s}</option>)}
                  </select>
                </div>
              )}
              {!['signature', 'qr'].includes(active.type) && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1" htmlFor="fp-font">Font</label>
                      <select id="fp-font" value={active.font} onChange={e => updateActive({ font: e.target.value })} className="w-full border border-slate-200 rounded px-2 py-1 text-xs">
                        <option value="helv">Helvetica</option>
                        <option value="helv-bold">Helvetica Bold</option>
                        <option value="times">Times</option>
                        <option value="times-bold">Times Bold</option>
                        <option value="courier">Courier</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1" htmlFor="fp-size">Size (pt)</label>
                      <input id="fp-size" type="number" min="6" max="96" value={active.size} onChange={e => updateActive({ size: Number(e.target.value) })} className="w-full border border-slate-200 rounded px-2 py-1 text-xs" data-testid="field-size-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1" htmlFor="fp-align">Align</label>
                      <select id="fp-align" value={active.align} onChange={e => updateActive({ align: e.target.value })} className="w-full border border-slate-200 rounded px-2 py-1 text-xs">
                        <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1" htmlFor="fp-color">Color</label>
                      <input id="fp-color" type="color" value={active.color} onChange={e => updateActive({ color: e.target.value })} className="w-full h-7 border border-slate-200 rounded" />
                    </div>
                  </div>
                </>
              )}
              <p className="text-[10px] text-slate-400">Position: {(active.x * 100).toFixed(1)}%, {(active.y * 100).toFixed(1)}% · Size: {(active.w * 100).toFixed(1)}% × {(active.h * 100).toFixed(1)}%. Long names shrink automatically to fit the box.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
